import { DndContext, DragOverlay, MouseSensor, TouchSensor, closestCenter, useDraggable, useDroppable, useSensor, useSensors, type Announcements, type DragEndEvent } from '@dnd-kit/core';
import { Check, GripVertical, Plus, X } from 'lucide-react';
import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { CHECKLIST_ITEM_MAX_LENGTH, CHECKLIST_TEXTS } from '@till-erp/collab/values';
import { apiFetch, ApiProblem, newIdempotencyKey, type ChecklistItem } from '../lib/api.ts';
import { useSession } from '../lib/session.tsx';
import { cn } from '../lib/utils.ts';
import type { UndoOffer } from './undo-toast.tsx';
import { CapsuleBar } from './ui/capsule-bar.tsx';
import { Textarea } from './ui/textarea.tsx';

/**
 * Checkliste einer Aufgabe (Story 23.3, FR-152, EXPERIENCE §4.22; Entwurf Variante A „Linear“
 * `ux-till-erp/entwuerfe-2026-10-29-checkliste-labels/`): Abschnitt im Blatt mit „2 von 5“ und Fortschrittsbalken, runde
 * Häkchen, Text zum Anklicken, Griff zum Umsortieren (Maus, Touch; Tastatur Pfeil hoch/runter am Griff) und „＋ Punkt hinzufügen“ (Enter = nächster). Jede Änderung speichert sofort, nacheinander (Warteschlange), und
 * bietet 10 s „Rückgängig“. Die Zeile zeigt die Mini-Fortschrittsanzeige (`ChecklistMini`).
 */

export const CHECKLIST_UI_TEXTS = {
  ...CHECKLIST_TEXTS,
  progressLabel: 'Fortschritt der Checkliste',
  complete: 'Alles abgehakt',
  next: '⏎ nächster',
  itemText: (i: number) => `Punkt ${i + 1}`,
  remove: (text: string) => `„${text}“ löschen`,
  move: (text: string) => `„${text}“ verschieben`,
  moveHint: 'Mit Pfeil hoch und runter verschieben, oder mit der Maus ziehen.',
  checked: (text: string) => `„${text}“ abgehakt`,
  unchecked: (text: string) => `„${text}“ wieder offen`,
  added: (text: string) => `„${text}“ hinzugefügt`,
  removed: (text: string) => `„${text}“ gelöscht`,
  renamed: 'Punkt geändert',
  moved: (text: string, to: number, total: number) => `„${text}“ an Stelle ${to} von ${total}`,
  movedUndo: (text: string) => `„${text}“ verschoben`,
  doneBy: (name: string) => `abgehakt von ${name}`,
  checklist: 'Checkliste',
} as const;

/** Mini-Fortschritt an Zeile und Karte: 36 × 4 px Balken und „2 von 5“ (grün, wenn alles abgehakt). */
export function ChecklistMini({ checklist, className }: { checklist: { done: number; total: number } | null; className?: string }) {
  if (!checklist || checklist.total === 0) return null;
  const complete = checklist.done === checklist.total;
  return (
    <span className={cn('inline-flex flex-none items-center gap-space-1 text-caption whitespace-nowrap text-ink-3 tabular-nums', className)} data-testid="checkliste-mini">
      <span aria-hidden className="relative h-1 w-9 overflow-hidden rounded-full bg-line">
        <span className={cn('absolute inset-y-0 left-0 rounded-full', complete ? 'bg-ready' : 'bg-capsule')} style={{ width: `${(checklist.done / checklist.total) * 100}%` }} />
      </span>
      <span className="sr-only">{CHECKLIST_UI_TEXTS.checklist}</span>
      {CHECKLIST_TEXTS.progress(checklist.done, checklist.total)}
    </span>
  );
}

interface Row {
  /** Kennung des Punkts oder vorläufige Kennung (`neu-1`), solange er angelegt wird. */
  key: string;
  text: string;
  done: boolean;
  doneByName: string | null;
}

function problemText(err: unknown): string {
  return err instanceof ApiProblem ? (Object.values(err.errors)[0] ?? err.detail) : 'Etwas ist schiefgelaufen.';
}

const TEMP = 'neu-';

export function TaskChecklist({
  taskId,
  items,
  stageDone,
  onChanged,
  offerUndo,
}: {
  taskId: string;
  items: ChecklistItem[];
  /** Aufgabe erledigt: Hinweis „n Punkte noch offen“. */
  stageDone: boolean;
  /** Blatt und Liste neu laden. */
  onChanged: () => Promise<unknown>;
  offerUndo: (o: UndoOffer) => void;
}) {
  const { getToken } = useSession();
  /** Sofort sichtbarer Stand, solange gespeichert wird; danach gilt wieder der Server. */
  const [override, setOverride] = useState<Row[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [justCompleted, setJustCompleted] = useState(false);
  // Haken nach dem letzten Punkt nur kurz zeigen (300 ms); bei „weniger Bewegung“ ohne Einblenden.
  useEffect(() => {
    if (!justCompleted) return;
    const t = window.setTimeout(() => setJustCompleted(false), 300);
    return () => window.clearTimeout(t);
  }, [justCompleted]);
  const [dragKey, setDragKey] = useState<string | null>(null);
  const [said, setSaid] = useState('');
  const queue = useRef<Promise<unknown>>(Promise.resolve());
  const running = useRef(0);
  const realId = useRef(new Map<string, string>());
  const tempCount = useRef(0);
  const addField = useRef<HTMLTextAreaElement>(null);
  const textRefs = useRef(new Map<string, HTMLTextAreaElement>());

  const rows: Row[] = override ?? items.map((i) => ({ key: i.id, text: i.text, done: i.done, doneByName: i.doneByName }));
  const done = rows.filter((r) => r.done).length;
  const total = rows.length;
  const complete = total > 0 && done === total;

  const post = <T,>(path: string, body: unknown, method: 'POST' | 'DELETE' = 'POST') =>
    apiFetch<T>(getToken, path, { method, ...(method === 'POST' ? { body } : {}), idempotencyKey: newIdempotencyKey() });
  const base = `/api/v1/tasks/${taskId}/checklist`;
  const idOf = (key: string) => realId.current.get(key) ?? key;

  /** Schritt in die Warteschlange; nach dem letzten Schritt Blatt neu laden und den Server-Stand zeigen. */
  const enqueue = (step: () => Promise<void>) => {
    running.current += 1;
    const run = async () => {
      try {
        await step();
      } catch (err) {
        setError(problemText(err));
      } finally {
        running.current -= 1;
        if (running.current === 0) {
          await onChanged().catch(() => undefined);
          if (running.current === 0) setOverride(null);
        }
      }
    };
    const next = queue.current.then(run, run);
    queue.current = next;
    return next;
  };

  /** Rückgängig: ebenfalls in die Warteschlange, ohne sofortige Anzeige (lädt danach neu). */
  const undoStep = (step: () => Promise<void>) => () =>
    new Promise<void>((resolve, reject) => {
      running.current += 1;
      const run = async () => {
        try {
          await step();
          resolve();
        } catch (err) {
          reject(err instanceof Error ? err : new Error(String(err)));
        } finally {
          running.current -= 1;
          if (running.current === 0) {
            await onChanged().catch(() => undefined);
            if (running.current === 0) setOverride(null);
          }
        }
      };
      queue.current = queue.current.then(run, run);
    });

  const show = (next: Row[]) => {
    setError(null);
    setOverride(next);
  };

  const add = () => {
    const text = draft.trim();
    if (text === '') return;
    tempCount.current += 1;
    const key = `${TEMP}${tempCount.current}`;
    show([...rows, { key, text, done: false, doneByName: null }]);
    setDraft('');
    setJustCompleted(false);
    void enqueue(async () => {
      const made = await post<{ id: string }>(base, { text });
      realId.current.set(key, made.id);
      offerUndo({ text: CHECKLIST_UI_TEXTS.added(text), undo: undoStep(() => post(`${base}/${made.id}`, undefined, 'DELETE').then(() => undefined)) });
    });
  };

  const toggle = (row: Row) => {
    const next = rows.map((r) => (r.key === row.key ? { ...r, done: !r.done } : r));
    show(next);
    setJustCompleted(!row.done && next.every((r) => r.done));
    const to = !row.done;
    void enqueue(async () => {
      const id = idOf(row.key);
      await post(`${base}/${id}/done`, { done: to });
      offerUndo({ text: to ? CHECKLIST_UI_TEXTS.checked(row.text) : CHECKLIST_UI_TEXTS.unchecked(row.text), undo: undoStep(() => post(`${base}/${id}/done`, { done: !to }).then(() => undefined)) });
    });
  };

  const rename = (row: Row, text: string) => {
    show(rows.map((r) => (r.key === row.key ? { ...r, text } : r)));
    void enqueue(async () => {
      const id = idOf(row.key);
      await post(`${base}/${id}/text`, { text });
      offerUndo({ text: CHECKLIST_UI_TEXTS.renamed, undo: undoStep(() => post(`${base}/${id}/text`, { text: row.text }).then(() => undefined)) });
    });
  };

  const remove = (row: Row) => {
    show(rows.filter((r) => r.key !== row.key));
    void enqueue(async () => {
      const gone = await post<{ text: string; position: number; done: boolean }>(`${base}/${idOf(row.key)}`, undefined, 'DELETE');
      offerUndo({
        text: CHECKLIST_UI_TEXTS.removed(row.text),
        undo: undoStep(() => post(base, { text: gone.text, position: gone.position, done: gone.done }).then(() => undefined)),
      });
    });
  };

  const move = (row: Row, to: number) => {
    const from = rows.findIndex((r) => r.key === row.key);
    if (from < 0 || to < 0 || to >= rows.length || to === from) return;
    const next = [...rows];
    next.splice(from, 1);
    next.splice(to, 0, row);
    show(next);
    setSaid(CHECKLIST_UI_TEXTS.moved(row.text, to + 1, rows.length));
    void enqueue(async () => {
      const id = idOf(row.key);
      await post(`${base}/${id}/position`, { position: to });
      offerUndo({ text: CHECKLIST_UI_TEXTS.movedUndo(row.text), undo: undoStep(() => post(`${base}/${id}/position`, { position: from }).then(() => undefined)) });
    });
  };

  const sensors = useSensors(useSensor(MouseSensor, { activationConstraint: { distance: 4 } }), useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }));
  const textOf = (key: string | number) => rows.find((r) => r.key === String(key))?.text ?? '';
  const indexOf = (key: string | number | undefined) => rows.findIndex((r) => r.key === String(key));
  const announcements: Announcements = {
    onDragStart: ({ active }) => `„${textOf(active.id)}“ angehoben.`,
    onDragOver: ({ active, over }) => (over ? CHECKLIST_UI_TEXTS.moved(textOf(active.id), indexOf(over.id) + 1, rows.length) : undefined),
    onDragEnd: ({ active, over }) => (over ? CHECKLIST_UI_TEXTS.moved(textOf(active.id), indexOf(over.id) + 1, rows.length) : `„${textOf(active.id)}“ bleibt an seiner Stelle.`),
    onDragCancel: ({ active }) => `„${textOf(active.id)}“ bleibt an seiner Stelle.`,
  };
  const onDragEnd = (e: DragEndEvent) => {
    setDragKey(null);
    const row = rows.find((r) => r.key === String(e.active.id));
    const to = indexOf(e.over?.id);
    if (row && to >= 0) move(row, to);
  };
  const dragged = dragKey ? rows.find((r) => r.key === dragKey) : undefined;
  const open = total - done;

  return (
    <section aria-label={CHECKLIST_UI_TEXTS.section} className="flex flex-col gap-space-2 border-t border-line pt-space-4" data-testid="aufgabe-checkliste">
      <div className="flex items-center gap-space-2">
        <span className="text-caption text-ink-3">{CHECKLIST_UI_TEXTS.section}</span>
        {total > 0 ? (
          <span className="ml-auto inline-flex items-center gap-space-1 text-caption text-ink-2 tabular-nums" data-testid="checkliste-stand">
            {complete && justCompleted ? <Check size={14} strokeWidth={2.5} aria-hidden className="animate-fade-in text-ready motion-reduce:animate-none" /> : null}
            {CHECKLIST_TEXTS.progress(done, total)}
          </span>
        ) : null}
      </div>
      {total > 0 ? (
        <CapsuleBar value={done / total} tone={complete ? 'ready' : 'accent'} label={CHECKLIST_UI_TEXTS.progressLabel} valueText={complete ? CHECKLIST_UI_TEXTS.complete : CHECKLIST_TEXTS.progress(done, total)} className="h-1" />
      ) : null}
      {stageDone && open > 0 ? (
        <p className="m-0 text-body text-check" data-testid="checkliste-offen">
          {CHECKLIST_TEXTS.openLeft(open)}
        </p>
      ) : null}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={(e) => setDragKey(String(e.active.id))}
        onDragEnd={onDragEnd}
        onDragCancel={() => setDragKey(null)}
        accessibility={{ announcements, screenReaderInstructions: { draggable: CHECKLIST_UI_TEXTS.moveHint } }}
      >
        <ol className="m-0 flex list-none flex-col p-0" data-testid="checkliste-punkte">
          {rows.map((row, i) => (
            <ChecklistRow
              key={row.key}
              row={row}
              index={i}
              count={rows.length}
              dragging={dragKey !== null}
              register={(el) => {
                if (el) textRefs.current.set(row.key, el);
                else textRefs.current.delete(row.key);
              }}
              onToggle={() => toggle(row)}
              onRename={(text) => rename(row, text)}
              onRemove={(focusPrevious) => {
                const prev = rows[i - 1];
                remove(row);
                if (focusPrevious) (prev ? textRefs.current.get(prev.key) : addField.current)?.focus();
              }}
              onMove={(to) => move(row, to)}
              onEnter={() => addField.current?.focus()}
            />
          ))}
        </ol>
        <DragOverlay>{dragged ? <div className="rounded-tile bg-card px-space-3 py-space-2 text-row text-ink shadow-float">{dragged.text}</div> : null}</DragOverlay>
      </DndContext>
      <span className="sr-only" aria-live="polite">
        {said}
      </span>

      <div className="group flex items-start gap-space-1 rounded-tile px-space-1">
        <span aria-hidden className="grid h-10 w-9 flex-none place-items-center text-ink-3 pointer-coarse:h-11">
          <Plus size={16} strokeWidth={2} />
        </span>
        <Textarea
          ref={addField}
          rows={1}
          value={draft}
          maxLength={CHECKLIST_ITEM_MAX_LENGTH}
          placeholder={CHECKLIST_TEXTS.add}
          aria-label={CHECKLIST_TEXTS.add}
          data-testid="checkliste-neu"
          onChange={(e) => setDraft(e.target.value.replace(/\n/g, ' '))}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
              e.preventDefault();
              add();
            }
            if (e.key === 'Escape' && draft !== '') {
              e.stopPropagation();
              setDraft('');
            }
          }}
          className="min-h-10 flex-1 resize-none border-transparent bg-transparent px-space-2 py-space-2 text-row field-sizing-content hover:enabled:not-focus:border-transparent focus:bg-card max-tablet:text-field-phone pointer-coarse:min-h-11"
        />
        <span aria-hidden className="hidden h-10 flex-none items-center text-caption text-ink-3 group-focus-within:flex max-tablet:group-focus-within:hidden">
          {CHECKLIST_UI_TEXTS.next}
        </span>
      </div>
      {error ? (
        <p role="alert" className="m-0 text-body text-clarify">
          {error}
        </p>
      ) : null}
    </section>
  );
}

function ChecklistRow({
  row,
  index,
  count,
  dragging,
  register,
  onToggle,
  onRename,
  onRemove,
  onMove,
  onEnter,
}: {
  row: Row;
  index: number;
  count: number;
  dragging: boolean;
  register: (el: HTMLTextAreaElement | null) => void;
  onToggle: () => void;
  onRename: (text: string) => void;
  onRemove: (focusPrevious: boolean) => void;
  onMove: (to: number) => void;
  onEnter: () => void;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const { attributes, listeners, setNodeRef: setDragNode, setActivatorNodeRef, isDragging } = useDraggable({ id: row.key });
  const { setNodeRef: setDropNode, isOver } = useDroppable({ id: row.key });
  const text = draft ?? row.text;
  const commit = () => {
    if (draft === null) return;
    const next = draft.trim();
    setDraft(null);
    if (next !== '' && next !== row.text) onRename(next);
  };
  const moveKeys = (e: KeyboardEvent) => {
    // Nur die reinen Pfeiltasten – Alt/Strg/Meta/Umschalt + Pfeil bleibt ohne Wirkung (keine eigenen Kürzel).
    if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      onMove(index + (e.key === 'ArrowUp' ? -1 : 1));
    }
  };
  return (
    <li
      ref={setDropNode}
      data-testid="checkliste-punkt"
      data-done={row.done ? 'true' : 'false'}
      className={cn(
        'group relative flex items-start gap-space-1 rounded-tile px-space-1 hover:bg-tile',
        isDragging && 'opacity-40',
        dragging && isOver && !isDragging && 'bg-tile ring-2 ring-brand',
      )}
    >
      <button
        type="button"
        role="checkbox"
        aria-checked={row.done}
        aria-label={row.text}
        title={row.done && row.doneByName ? CHECKLIST_UI_TEXTS.doneBy(row.doneByName) : undefined}
        onClick={onToggle}
        className="grid h-10 w-9 flex-none cursor-pointer place-items-center pointer-coarse:size-11"
      >
        <span
          aria-hidden
          className={cn(
            'grid size-5 place-items-center rounded-full border-[1.5px] transition-colors duration-(--motion-fast) ease-(--motion-ease) motion-reduce:transition-none pointer-coarse:size-6',
            row.done ? 'border-brand bg-brand text-on-brand' : 'border-chip-line bg-card group-hover:border-field-line',
          )}
        >
          {row.done ? <Check size={12} strokeWidth={3} /> : null}
        </span>
      </button>
      <Textarea
        ref={register}
        rows={1}
        value={text}
        maxLength={CHECKLIST_ITEM_MAX_LENGTH}
        aria-label={CHECKLIST_UI_TEXTS.itemText(index)}
        onChange={(e) => setDraft(e.target.value.replace(/\n/g, ' '))}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
            e.preventDefault();
            // Speichern übernimmt allein `onBlur` (Fokus wandert ins Feld „Punkt hinzufügen“) – sonst zweimal Umbenennen.
            const field = e.currentTarget;
            onEnter();
            if (document.activeElement === field) field.blur();
          } else if (e.key === 'Escape' && draft !== null) {
            e.stopPropagation();
            setDraft(null);
          } else if (e.key === 'Backspace' && text === '') {
            e.preventDefault();
            setDraft(null);
            onRemove(true);
          }
        }}
        className={cn(
          'min-h-10 flex-1 resize-none border-transparent bg-transparent px-space-2 py-space-2 text-row field-sizing-content hover:enabled:not-focus:border-transparent focus:bg-card max-tablet:text-field-phone pointer-coarse:min-h-11',
          row.done && draft === null && 'text-ink-3 line-through',
        )}
      />
      <span className="flex flex-none items-center opacity-0 group-focus-within:opacity-100 group-hover:opacity-100 pointer-coarse:opacity-100">
        <button
          type="button"
          aria-label={CHECKLIST_UI_TEXTS.remove(row.text)}
          onClick={() => onRemove(false)}
          className="grid h-10 w-8 cursor-pointer place-items-center rounded-full text-ink-3 hover:text-clarify pointer-coarse:size-11"
        >
          <X size={16} strokeWidth={2} aria-hidden />
        </button>
        <button
          type="button"
          ref={setActivatorNodeRef}
          {...attributes}
          {...listeners}
          aria-label={CHECKLIST_UI_TEXTS.move(row.text)}
          aria-roledescription={undefined}
          aria-pressed={undefined}
          onKeyDown={(e) => {
            moveKeys(e);
          }}
          disabled={count < 2}
          data-testid="checkliste-griff"
          className="grid h-10 w-8 cursor-grab touch-none place-items-center text-ink-3 hover:text-ink disabled:cursor-default disabled:opacity-40 pointer-coarse:size-11"
        >
          <GripVertical size={16} strokeWidth={2} aria-hidden />
        </button>
      </span>
      {/* Ziehen: die ganze Zeile ist Ablageziel; der Griff ist der Anfasser. */}
      <span ref={setDragNode} aria-hidden className="pointer-events-none absolute inset-0" />
    </li>
  );
}
